import { useState, useMemo, useCallback } from 'react';
import type { EventMember, EventTable, SfdcUser } from '../event-manager-client';

interface SeatAssignment {
  tableId: number | null;
  seatPosition: number | null;
}

// Type alias (not interface) so it stays assignable to the Record<string, unknown>
// fields param of onPatchMember.
type MetWithResult = {
  primary_met_with: string | null;
  secondary_met_with: string | null;
  tertiary_met_with: string | null;
};

export function useSeatingChart(members: EventMember[], tables: EventTable[]) {
  const [pending, setPending] = useState<Map<number, SeatAssignment>>(new Map());

  const effectiveAssignments = useMemo(() => {
    const map = new Map<number, SeatAssignment>();
    for (const m of members) {
      map.set(m.id, { tableId: m.table_id, seatPosition: m.seat_position });
    }
    for (const [memberId, assignment] of pending) {
      map.set(memberId, assignment);
    }
    return map;
  }, [members, pending]);

  const tableMembers = useMemo(() => {
    const map = new Map<number, EventMember[]>();
    for (const t of tables) map.set(t.id, []);
    for (const m of members) {
      const assignment = effectiveAssignments.get(m.id);
      if (assignment?.tableId != null) {
        const list = map.get(assignment.tableId);
        if (list) list.push(m);
      }
    }
    // Sort each table's members by seat position
    for (const list of map.values()) {
      list.sort((a, b) => {
        const posA = effectiveAssignments.get(a.id)?.seatPosition ?? Infinity;
        const posB = effectiveAssignments.get(b.id)?.seatPosition ?? Infinity;
        return posA - posB;
      });
    }
    return map;
  }, [members, tables, effectiveAssignments]);

  const unassignedMembers = useMemo(() => {
    return members.filter(m => {
      const assignment = effectiveAssignments.get(m.id);
      return assignment?.tableId == null;
    });
  }, [members, effectiveAssignments]);

  const moveMember = useCallback((memberId: number, targetTableId: number | null, seatPosition?: number | null) => {
    setPending(prev => {
      const next = new Map(prev);

      if (targetTableId != null && seatPosition != null) {
        // Check if target seat is occupied — if so, swap
        const occupant = members.find(m => {
          if (m.id === memberId) return false;
          const a = next.has(m.id)
            ? next.get(m.id)!
            : { tableId: m.table_id, seatPosition: m.seat_position };
          return a.tableId === targetTableId && a.seatPosition === seatPosition;
        });

        if (occupant) {
          // Swap: occupant goes to mover's current position
          const moverCurrent = next.has(memberId)
            ? next.get(memberId)!
            : { tableId: members.find(m => m.id === memberId)?.table_id ?? null, seatPosition: members.find(m => m.id === memberId)?.seat_position ?? null };
          next.set(occupant.id, { tableId: moverCurrent.tableId, seatPosition: moverCurrent.seatPosition });
        }
      }

      // Enforce table capacity — reject if table is full (unless it's a swap or the member is already at that table)
      if (targetTableId != null) {
        const table = tables.find(t => t.id === targetTableId);
        if (table) {
          // Count members currently at this table (including pending moves) excluding the member being moved
          let count = 0;
          for (const m of members) {
            if (m.id === memberId) continue;
            const a = next.has(m.id) ? next.get(m.id)! : { tableId: m.table_id, seatPosition: m.seat_position };
            if (a.tableId === targetTableId) count++;
          }
          if (count >= table.capacity) return prev; // Table full — reject
        }
      }

      next.set(memberId, { tableId: targetTableId, seatPosition: seatPosition ?? null });
      return next;
    });
  }, [members, tables]);

  const clearPending = useCallback(() => {
    setPending(new Map());
  }, []);

  const clearPendingForTable = useCallback((tableId: number) => {
    setPending(prev => {
      const next = new Map(prev);
      for (const [memberId, assignment] of prev) {
        // Clear pending moves TO this table (table is being deleted)
        if (assignment.tableId === tableId) next.delete(memberId);
      }
      // Also clear members whose DB position is at this table (they had pending moves elsewhere,
      // but the API delete will unassign them anyway)
      for (const m of members) {
        if (m.table_id === tableId && next.has(m.id)) next.delete(m.id);
      }
      return next;
    });
  }, [members]);

  const isDirty = pending.size > 0;

  const pendingChanges = useMemo(() => {
    const changes: { memberId: number; tableId: number | null; seatPosition: number | null }[] = [];
    for (const [memberId, assignment] of pending) {
      changes.push({ memberId, ...assignment });
    }
    return changes;
  }, [pending]);

  return {
    effectiveAssignments,
    tableMembers,
    unassignedMembers,
    moveMember,
    clearPending,
    clearPendingForTable,
    isDirty,
    pending,
    pendingChanges,
  };
}

/**
 * Compute met-with assignments based on table seating.
 * Clay employees are identified by matching contact_email against sfdcUsers[].email.
 * Primary: Clay employee at same table.
 * Secondary/Tertiary: Clay employees at adjacent tables (sort_order ± 1).
 */
export function computeMetWith(
  members: EventMember[],
  tables: EventTable[],
  sfdcUsers: SfdcUser[],
  effectiveAssignments: Map<number, { tableId: number | null; seatPosition: number | null }>,
): Map<number, MetWithResult> {
  const result = new Map<number, MetWithResult>();

  // Build email → userId lookup
  const emailToUserId = new Map<string, string>();
  for (const u of sfdcUsers) {
    emailToUserId.set(u.email.toLowerCase(), u.userId);
  }

  // Sort tables by sort_order for adjacency
  const sortedTables = [...tables].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  const tableIndex = new Map<number, number>(); // tableId → index in sortedTables
  for (let i = 0; i < sortedTables.length; i++) {
    tableIndex.set(sortedTables[i].id, i);
  }

  // Group members by table
  const membersAtTable = new Map<number, EventMember[]>();
  for (const m of members) {
    const a = effectiveAssignments.get(m.id);
    if (a?.tableId != null) {
      if (!membersAtTable.has(a.tableId)) membersAtTable.set(a.tableId, []);
      membersAtTable.get(a.tableId)!.push(m);
    }
  }

  // Find Clay employee userIds at each table
  const clayAtTable = new Map<number, string[]>();
  for (const [tableId, tableMembers] of membersAtTable) {
    const clayIds: string[] = [];
    for (const m of tableMembers) {
      if (m.contact_email) {
        const userId = emailToUserId.get(m.contact_email.toLowerCase());
        if (userId) clayIds.push(userId);
      }
    }
    clayAtTable.set(tableId, clayIds);
  }

  // Compute met-with for each non-Clay member at a table
  for (const m of members) {
    const a = effectiveAssignments.get(m.id);
    if (a?.tableId == null) continue;

    // Skip Clay employees
    if (m.contact_email && emailToUserId.has(m.contact_email.toLowerCase())) continue;

    const idx = tableIndex.get(a.tableId);
    if (idx === undefined) continue;

    const sameTableClay = clayAtTable.get(a.tableId) ?? [];

    // Adjacent tables
    const adjacentClay: string[] = [];
    if (idx > 0) {
      const adjTable = sortedTables[idx - 1];
      adjacentClay.push(...(clayAtTable.get(adjTable.id) ?? []));
    }
    if (idx < sortedTables.length - 1) {
      const adjTable = sortedTables[idx + 1];
      adjacentClay.push(...(clayAtTable.get(adjTable.id) ?? []));
    }

    // Fill primary/secondary/tertiary from same-table Clay first, then adjacent
    const allCandidates = [...sameTableClay, ...adjacentClay];
    const primary = allCandidates[0] ?? null;
    const secondary = allCandidates[1] ?? null;
    const tertiary = allCandidates[2] ?? null;

    const newVal: MetWithResult = {
      primary_met_with: primary,
      secondary_met_with: secondary,
      tertiary_met_with: tertiary,
    };

    // Only include if different from current values
    if (
      newVal.primary_met_with !== (m.primary_met_with ?? null) ||
      newVal.secondary_met_with !== (m.secondary_met_with ?? null) ||
      newVal.tertiary_met_with !== (m.tertiary_met_with ?? null)
    ) {
      result.set(m.id, newVal);
    }
  }

  return result;
}
